import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";

import { color } from "@/lib/theme";

const NODES = [
  { id: "agent", label: "AI System", sub: "Customer Support Agent · DEMO", type: "system", x: 7, y: 50, meta: "SYS-DEMO-04", detail: "A demonstration customer-facing AI agent connected to a model, a dataset and a knowledge-base tool. The root of the graph. Demonstration data." },
  { id: "model", label: "Model", sub: "Support LLM", type: "model", x: 22, y: 18, meta: "MDL-DEMO-11", detail: "The underlying model powering the agent’s responses." },
  { id: "data", label: "Dataset", sub: "Customer records", type: "data", x: 22, y: 50, meta: "DAT-DEMO-02", detail: "Records the agent can read to answer questions." },
  { id: "tool", label: "Tool", sub: "Knowledge Base", type: "tool", x: 22, y: 82, meta: "TL-DEMO-07", detail: "A retrieval tool the agent uses for grounded answers." },
  { id: "assessment", label: "Assessment", sub: "AI Red-Team Assessment", type: "assessment", x: 38, y: 50, meta: "ASM-DEMO-0814", detail: "A dated security + governance assessment run in a controlled test environment." },
  { id: "finding", label: "Finding", sub: "Prompt Injection · HIGH", type: "finding", x: 55, y: 16, meta: "FND-DEMO-0042", detail: "The agent could be manipulated into revealing its system prompt. High severity — now mitigated. Demo finding." },
  { id: "risk", label: "Risk", sub: "System prompt exposure", type: "risk", x: 55, y: 50, meta: "RSK-DEMO-0009", detail: "The business risk the finding maps to, tracked with an owner until it is controlled." },
  { id: "control", label: "Control", sub: "AI Input Validation", type: "control", x: 55, y: 84, meta: "CTRL-A6.2", detail: "An input-validation control, mapped to ISO/IEC 42001 A.6.2, that mitigates the risk." },
  { id: "evidence", label: "Evidence", sub: "Evidence package", type: "evidence", x: 72, y: 50, meta: "EV-DEMO-00481", detail: "Test log + remediation report — dated and recorded, linking the control to the finding it resolves." },
  { id: "framework", label: "Requirement", sub: "ISO/IEC 42001", type: "framework", x: 86, y: 20, meta: "Clause 8", detail: "The recognized framework requirement the control maps to, feeding readiness." },
  { id: "certificate", label: "Certificate", sub: "CERT-DEMO-000148", type: "certificate", x: 88, y: 80, meta: "CERT-DEMO-000148", detail: "An independently verifiable Meira assurance record for everything assessed — checkable by anyone, no login." },
  { id: "verified", label: "Verified", sub: "Current status", type: "verified", x: 97, y: 50, meta: "STATUS · VERIFIED", detail: "The assurance record’s current, verifiable status." },
];

const EDGES = [
  ["agent", "model"], ["agent", "data"], ["agent", "tool"], ["agent", "assessment"],
  ["assessment", "finding"], ["finding", "risk"], ["risk", "control"], ["control", "evidence"],
  ["evidence", "framework"], ["evidence", "certificate"], ["framework", "certificate"], ["certificate", "verified"],
];

// Palette tokens, not colours — resolved through `color()` at each use site so
// the graph follows the scheme. A node that carries a judgement takes a status
// token; the rest of the graph is brand furniture. Evidence and the certificate
// are gold because they are the proof moment.
const typeColor = {
  system: "brand", model: "brand", data: "brand", tool: "brand",
  assessment: "brand", finding: "warn", risk: "danger", control: "brand-deep",
  evidence: "gold", framework: "brand", certificate: "gold", verified: "valid",
};

const byId = (id) => NODES.find((n) => n.id === id);

// the linear evidence chain used for mobile + accessibility
const CHAIN = ["agent", "assessment", "finding", "risk", "control", "evidence", "framework", "certificate", "verified"];

export default function TrustGraph() {
  const [hover, setHover] = useState(null);
  const [pinned, setPinned] = useState("certificate");
  const active = hover || pinned;

  const related = useMemo(() => {
    if (!active) return null;
    const up = new Set(), down = new Set();
    const walk = (id, dir, seen) => {
      EDGES.forEach(([a, b]) => {
        if (dir === "down" && a === id && !seen.has(b)) { seen.add(b); walk(b, dir, seen); }
        if (dir === "up" && b === id && !seen.has(a)) { seen.add(a); walk(a, dir, seen); }
      });
    };
    walk(active, "down", down);
    walk(active, "up", up);
    return new Set([active, ...up, ...down]);
  }, [active]);

  const activeNode = byId(active) || byId("certificate");
  const isOn = (id) => !related || related.has(id);
  const edgeOn = (a, b) => related && related.has(a) && related.has(b);

  return (
    <div className="grid gap-8 lg:grid-cols-[1.7fr_1fr] lg:items-stretch">
      {/* desktop graph */}
      <div className="relative hidden aspect-[16/10] w-full rounded-lg border border-line bg-panel-raised/50 tech-grid lg:block">
        <svg className="absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
          {EDGES.map(([a, b]) => {
            const na = byId(a), nb = byId(b), on = edgeOn(a, b);
            return (
              <motion.line
                key={`${a}-${b}`}
                x1={na.x} y1={na.y} x2={nb.x} y2={nb.y}
                stroke={on ? color("brand") : color("line")}
                strokeWidth={on ? 0.5 : 0.28}
                vectorEffect="non-scaling-stroke"
                initial={{ pathLength: 0, opacity: 0 }}
                whileInView={{ pathLength: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
                style={on ? { filter: `drop-shadow(0 0 3px ${color("brand", 0.55)})` } : undefined}
              />
            );
          })}
        </svg>

        {NODES.map((n, i) => {
          const tone = typeColor[n.type];
          const on = isOn(n.id);
          const isActive = n.id === active;
          return (
            // Position and the traced/dimmed state live on a static wrapper: framer-motion
            // writes inline `transform` and `opacity`, either of which would win over the
            // Tailwind -translate-*-1/2 and the `on` dim set here.
            <div
              key={n.id}
              className="absolute -translate-x-1/2 -translate-y-1/2"
              style={{ left: `${n.x}%`, top: `${n.y}%`, opacity: on ? 1 : 0.28, transition: "opacity 300ms" }}
            >
              <motion.button
                data-testid={`trust-node-${n.id}`}
                aria-label={`${n.label}: ${n.sub}`}
                onMouseEnter={() => setHover(n.id)}
                onMouseLeave={() => setHover(null)}
                onFocus={() => setHover(n.id)}
                onBlur={() => setHover(null)}
                onClick={() => setPinned(n.id)}
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.15 + i * 0.06, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className="group block focus:outline-none"
              >
                <span className="relative flex flex-col items-center">
                  <span
                    className="relative flex h-3.5 w-3.5 items-center justify-center rounded-full transition-transform duration-300 group-hover:scale-125"
                    style={{ background: color(tone), boxShadow: isActive ? `0 0 12px ${color(tone, 0.45)}` : `0 0 5px ${color(tone, 0.3)}` }}
                  >
                    {isActive && <span className="absolute inset-0 animate-pulse-ring rounded-full" style={{ background: color(tone) }} />}
                  </span>
                  <span className={`mt-2 whitespace-nowrap font-mono text-[9px] uppercase tracking-[0.12em] ${isActive ? "text-content" : "text-content-muted"}`}>
                    {n.label}
                  </span>
                </span>
              </motion.button>
            </div>
          );
        })}
        <div className="pointer-events-none absolute bottom-3 left-4 font-mono text-[9px] tracking-wide text-content-faint">Hover to trace · click to pin</div>
      </div>

      {/* mobile guided chain (accessible alternative) */}
      <ol className="flex flex-col gap-0 lg:hidden" aria-label="Trust graph evidence chain">
        <li className="pb-4 font-mono text-[9px] uppercase tracking-[0.2em] text-content-faint">Evidence chain</li>
        {CHAIN.map((id, i) => {
          const n = byId(id);
          return (
            <li key={id} className="relative pl-8">
              <span className="absolute left-[7px] top-1.5 h-2.5 w-2.5 rounded-full" style={{ background: color(typeColor[n.type]) }} />
              {i < CHAIN.length - 1 && <span className="absolute left-3 top-4 h-full w-px bg-line" />}
              <div className="pb-6">
                <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-content-faint">{n.label}</div>
                <div className="text-sm text-content">{n.sub}</div>
              </div>
            </li>
          );
        })}
      </ol>

      {/* detail panel */}
      <motion.div
        key={active}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
        className="relative flex flex-col overflow-hidden rounded-lg border border-line bg-panel-raised p-5 sm:p-7"
      >
        <span className="absolute left-0 top-0 h-full w-[3px]" style={{ background: color(typeColor[activeNode.type]) }} />
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-content-faint">{activeNode.type} node</div>
        <h4 className="mt-3 font-display text-2xl font-medium text-content">{activeNode.label}</h4>
        <div className="mt-1 text-sm text-brand-soft">{activeNode.sub}</div>
        <p className="mt-4 text-sm leading-relaxed text-content-muted">{activeNode.detail}</p>
        <div className="mt-5 inline-flex w-fit rounded-full border border-line bg-panel-raised px-3 py-1 font-mono text-[10px] tracking-wide text-content-muted">
          {activeNode.meta}
        </div>
      </motion.div>
    </div>
  );
}
